import type { Vehicle } from '@local/common-lib';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import {
  Button,
  CardActions,
  CardContent,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { FC } from 'react';
import { CenterCard } from '../../components/page-layout/center-card.js';

interface Props {
  loading?: boolean;
  onAddClick: () => void;
  onDeleteClick: (vehicle: Vehicle.Type) => void;
  onEditClick: (vehicle: Vehicle.Type) => void;
  vehicles: Vehicle.Type[];
}

export const VehicleListView: FC<Props> = ({
  loading,
  onAddClick,
  onDeleteClick,
  onEditClick,
  vehicles,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  if (!vehicles.length) {
    return (
      <CenterCard>
        <CardContent>
          <Stack direction="column" spacing={2}>
            <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No vehicles registered yet.
            </Typography>
          </Stack>
        </CardContent>
        <CardActions>
          <Button onClick={onAddClick} variant="contained">
            Add Vehicle
          </Button>
        </CardActions>
      </CenterCard>
    );
  }

  if (isSmallScreen) {
    return (
      <Stack direction="column" spacing={2}>
        <CardActions>
          <Button onClick={onAddClick} variant="contained">
            Add Vehicle
          </Button>
        </CardActions>
        {vehicles.map((vehicle) => (
          <Paper key={vehicle.rego} sx={{ p: 2 }}>
            <Stack direction="column" spacing={1}>
              <Stack
                alignItems="center"
                direction="row"
                justifyContent="space-between"
              >
                <Typography variant="h6">{vehicle.rego}</Typography>
                <Stack direction="row" spacing={0}>
                  <IconButton onClick={() => onEditClick(vehicle)} size="small">
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => onDeleteClick(vehicle)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </Stack>
              <Typography variant="body2">
                {vehicle.make} {vehicle.model}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                {vehicle.color}
                {vehicle.hasLivery && ' • Has Livery'}
              </Typography>
            </Stack>
          </Paper>
        ))}
      </Stack>
    );
  }

  return (
    <Stack direction="column" spacing={2}>
      <CardActions>
        <Button onClick={onAddClick} variant="contained">
          Add Vehicle
        </Button>
      </CardActions>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Registration</TableCell>
            <TableCell>Make</TableCell>
            <TableCell>Model</TableCell>
            <TableCell>Color</TableCell>
            <TableCell>Livery</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {vehicles.map((vehicle) => (
            <TableRow key={vehicle.rego}>
              <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                {vehicle.rego}
              </TableCell>
              <TableCell>{vehicle.make}</TableCell>
              <TableCell>{vehicle.model}</TableCell>
              <TableCell>{vehicle.color}</TableCell>
              <TableCell>{vehicle.hasLivery ? 'Yes' : 'No'}</TableCell>
              <TableCell align="right">
                <Stack direction="row" justifyContent="flex-end" spacing={0}>
                  <IconButton onClick={() => onEditClick(vehicle)} size="small">
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => onDeleteClick(vehicle)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Stack>
  );
};
